import React, { useRef, useState } from 'react';
import { useTranslation } from '../contexts/LocaleContext';

type FileDropzoneProps = {
  value: File | File[] | null;
  onChange: (value: File | File[] | null) => void;
  multiple?: boolean;
  accept?: string;
};

const FileDropzone: React.FC<FileDropzoneProps> = ({
  value,
  onChange,
  multiple = false,
  accept = 'image/*,video/*',
}) => {
  const t = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const files: File[] = value == null ? [] : Array.isArray(value) ? value : [value];
  const hasFiles = files.length > 0;

  const handleSelect = (selected: FileList | null) => {
    if (!selected || selected.length === 0) {
      onChange(multiple ? [] : null);
      return;
    }
    const arr = Array.from(selected);
    if (multiple) {
      onChange(arr);
    } else {
      onChange(arr[0]);
    }
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="d-none"
        onChange={(e) => handleSelect(e.target.files)}
      />
      <div
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            fileInputRef.current?.click();
          }
        }}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragging(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragging(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsDragging(false);
          handleSelect(e.dataTransfer?.files ?? null);
        }}
        style={{
          border: `2px dashed ${isDragging ? 'var(--bs-primary)' : '#dee2e6'}`,
          borderRadius: 8,
          padding: '1.5rem',
          textAlign: 'center',
          cursor: 'pointer',
          backgroundColor: isDragging ? 'rgba(13, 110, 253, 0.08)' : 'transparent',
          transition: 'border-color 0.15s, background-color 0.15s',
        }}
      >
        {hasFiles ? (
          <span className="text-muted">
            {multiple && files.length > 1
              ? `${files.length} ${t('formField.files') || 'файлов'}`
              : files[0].name}
          </span>
        ) : (
          <span className="text-muted">
            {t('formField.dragndrop') || 'Перетащите файл сюда или нажмите для выбора'}
          </span>
        )}
      </div>
    </>
  );
};

export default FileDropzone;
